/**
 * @file CorsTelemetry.js
 * @description Sistema de telemetría avanzado para monitoreo de eventos CORS
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

/**
 * @class CorsTelemetry
 * @description Implementa un sistema de telemetría para rastrear eventos CORS y proxy
 * siguiendo el patrón Observer para desacoplar eventos de sus handlers
 */
class CorsTelemetry {
  constructor() {
    this.events = [];
    this.maxEvents = 100;
    this.metricsSnapshot = {
      totalProxiedRequests: 0,
      totalErrorsDetected: 0,
      totalRetries: 0,
      domainsWithErrors: new Set(),
      lastEventTimestamp: null
    };
    this.subscribers = [];
    
    // Intentar cargar eventos previos del localStorage
    this._loadEventsFromStorage();
    
    // Configurar intervalo para persistencia automática
    if (typeof window !== 'undefined') {
      this.persistInterval = setInterval(() => this._persistEvents(), 60000);
    }
  }
  
  /**
   * Registra un evento de telemetría CORS
   * @param {string} eventType - Tipo de evento (request, error, retry, proxy)
   * @param {Object} data - Datos asociados al evento
   */
  trackEvent(eventType, data = {}) {
    if (!eventType) return;
    
    // Crear objeto de evento
    const event = {
      type: eventType,
      timestamp: Date.now(),
      data: this._sanitizeData(data)
    };
    
    // Añadir a la colección de eventos
    this.events.push(event);
    
    // Actualizar métricas en tiempo real
    this._updateMetrics(event);
    
    // Notificar a los suscriptores
    this._notifySubscribers(event);
    
    // Limitar el tamaño de la colección
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }
    
    return event;
  }
  
  /**
   * Registra una petición que usó el proxy CORS
   * @param {string} url - URL original de la petición
   * @param {string} domain - Dominio de la petición
   * @param {string} reason - Razón por la que se usó el proxy
   */
  trackProxiedRequest(url, domain, reason = 'unknown') {
    return this.trackEvent('proxy_request', {
      url,
      domain,
      reason
    });
  }
  
  /**
   * Registra un error CORS detectado
   * @param {string} url - URL que causó el error
   * @param {string} domain - Dominio que causó el error
   * @param {string} errorMessage - Mensaje de error
   * @param {Object} responseData - Datos de la respuesta si están disponibles
   */
  trackCorsError(url, domain, errorMessage, responseData = null) {
    return this.trackEvent('cors_error', {
      url,
      domain,
      errorMessage,
      responseData
    });
  }
  
  /**
   * Registra un reintento mediante proxy después de un error CORS
   * @param {string} url - URL original
   * @param {string} domain - Dominio de la petición
   * @param {boolean} success - Si el reintento fue exitoso
   */
  trackRetry(url, domain, success) {
    return this.trackEvent('retry', {
      url,
      domain,
      success,
      timestamp: Date.now()
    });
  }
  
  /**
   * Suscribe una función para recibir notificaciones de eventos
   * @param {Function} callback - Función a llamar con cada evento
   * @returns {Function} Función para cancelar la suscripción
   */
  subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    
    this.subscribers.push(callback);
    
    // Devolver función para cancelar suscripción
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }
  
  /**
   * Obtiene un resumen de las métricas de telemetría
   * @returns {Object} Resumen de métricas
   */
  getMetricsSummary() {
    return {
      ...this.metricsSnapshot,
      domainsWithErrors: Array.from(this.metricsSnapshot.domainsWithErrors),
      eventCount: this.events.length,
      lastUpdate: new Date().toISOString()
    };
  }
  
  /**
   * Exporta los datos de telemetría para análisis
   * @returns {Object} Datos para exportar
   */
  exportTelemetryData() {
    return {
      metrics: this.getMetricsSummary(),
      events: this.events.slice(-50), // Últimos 50 eventos
      timestamp: new Date().toISOString(),
      appVersion: process.env.REACT_APP_VERSION || 'unknown'
    };
  }
  
  /**
   * Sanitiza datos sensibles antes de almacenarlos
   * @private
   * @param {Object} data - Datos a sanitizar
   * @returns {Object} Datos sanitizados
   */
  _sanitizeData(data) {
    const sanitized = { ...data };
    
    // Sanitizar URLs para eliminar tokens
    if (sanitized.url && typeof sanitized.url === 'string') {
      try {
        const url = new URL(sanitized.url);
        // Eliminar parámetros sensibles
        ['token', 'access_token', 'id_token', 'refresh_token', 'secret'].forEach(param => {
          url.searchParams.delete(param);
        });
        sanitized.url = url.toString();
      } catch (e) {
        // Si la URL no es válida, no hacer nada
      }
    }
    
    // Sanitizar cabeceras de autenticación
    if (sanitized.headers && sanitized.headers.authorization) {
      sanitized.headers = { ...sanitized.headers };
      sanitized.headers.authorization = '[REDACTED]';
    }
    
    return sanitized;
  }
  
  /**
   * Actualiza las métricas en tiempo real
   * @private
   * @param {Object} event - Evento a procesar
   */
  _updateMetrics(event) {
    this.metricsSnapshot.lastEventTimestamp = event.timestamp;
    
    switch (event.type) {
      case 'proxy_request':
        this.metricsSnapshot.totalProxiedRequests++;
        break;
      case 'cors_error':
        this.metricsSnapshot.totalErrorsDetected++;
        if (event.data.domain) {
          this.metricsSnapshot.domainsWithErrors.add(event.data.domain);
        }
        break;
      case 'retry':
        this.metricsSnapshot.totalRetries++;
        break;
    }
  }
  
  /**
   * Notifica a todos los suscriptores sobre un nuevo evento
   * @private
   * @param {Object} event - Evento a notificar
   */
  _notifySubscribers(event) {
    for (const callback of this.subscribers) {
      try {
        callback(event);
      } catch (e) {
        console.error('[CorsTelemetry] Error en suscriptor:', e);
      }
    }
  }
  
  /**
   * Carga eventos previos del localStorage
   * @private
   */
  _loadEventsFromStorage() {
    if (typeof localStorage === 'undefined') return;
    
    try {
      const storedEvents = localStorage.getItem('nubelytics_cors_telemetry');
      if (storedEvents) {
        const parsedEvents = JSON.parse(storedEvents);
        if (Array.isArray(parsedEvents) && parsedEvents.length > 0) {
          // Solo cargar los últimos 50 eventos para no sobrecargar la memoria
          this.events = parsedEvents.slice(-50);
          
          // Recalcular métricas
          this.events.forEach(event => this._updateMetrics(event));
        }
      }
    } catch (e) {
      console.error('[CorsTelemetry] Error al cargar eventos:', e);
    }
  }
  
  /**
   * Persiste eventos en localStorage
   * @private
   */
  _persistEvents() {
    if (typeof localStorage === 'undefined') return;
    
    try {
      localStorage.setItem('nubelytics_cors_telemetry', JSON.stringify(this.events));
    } catch (e) {
      console.error('[CorsTelemetry] Error al persistir eventos:', e);
      
      // Si hay error por tamaño, reducir la cantidad de eventos
      if (e.name === 'QuotaExceededError') {
        this.events = this.events.slice(-50);
        try {
          localStorage.setItem('nubelytics_cors_telemetry', JSON.stringify(this.events));
        } catch (e2) {
          // Si aún falla, limpiar completamente
          this.events = [];
        }
      }
    }
  }
  
  /**
   * Limpia todos los datos de telemetría
   */
  clearTelemetryData() {
    this.events = [];
    this.metricsSnapshot = {
      totalProxiedRequests: 0,
      totalErrorsDetected: 0,
      totalRetries: 0,
      domainsWithErrors: new Set(),
      lastEventTimestamp: null
    };
    
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('nubelytics_cors_telemetry');
    }
  }
}

// Exportar como singleton
const corsTelemetry = new CorsTelemetry();
export default corsTelemetry;
